/* eslint-disable no-unused-vars */
import React, { useState, useRef, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import FeatherIcon from 'feather-icons-react/build/FeatherIcon';
import { Controller, useForm } from 'react-hook-form';
import {
  Button,
  Col,
  Form,
  Input,
  notification,
  Row,
  Select,
  Space,
  Spin,
  Typography,
} from 'antd';
import apiService from '../../../axios/apiService';
import { useUserProfile } from '../../../hooks/useUserProfile';
import ApiService from '../../../services/ApiService';
import ChangeUserPassword from './ChangeUserPassword';
import { imguser } from '../../imagepath';

const EditProfile = () => {
  const { userProfile, setUserProfile } = useUserProfile();
  const [api, contextHolder] = notification.useNotification();
   const navigate = useNavigate();
  const {
    register,
    formState: { errors },
    control,
    handleSubmit,
    reset,
  } = useForm({
    values: userProfile || {},
  });
  const [loading, setLoading] = useState(false);
  const [changePasswordVisible, setChangePasswordVisible] = useState(false);
  const [profileImage, setProfileImage] = useState(null);
  const [profileImagePreview, setProfileImagePreview] = useState(imguser);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadedImageKey, setUploadedImageKey] = useState(null);
  const [isImageRemoved, setIsImageRemoved] = useState(false);
  const fileInputRef = useRef(null);

  // Memoize display image to reduce render cycles
  const displayImage = useMemo(() => {
    // If user has selected a new image (has profileImage or uploadedImageKey), show preview
    if (profileImage || uploadedImageKey) {
      return profileImagePreview;
    }
    // Otherwise, show the profile image from API or fallback
    const profileImageUrl =
      userProfile && userProfile.profileImageUrl
        ? userProfile.profileImageUrl
        : null;
    return profileImageUrl || imguser;
  }, [
    profileImagePreview,
    userProfile && userProfile.profileImageUrl,
    profileImage,
    uploadedImageKey,
  ]);

  const toggleChangePassword = () => {
    if (!changePasswordVisible) {
      reset();
      ApiService.forgetPassword(userProfile?.email);
    }
    setChangePasswordVisible((prevState) => !prevState);
  };

  const handleImageClick = () => {
    fileInputRef.current?.click();
  };

  const handleImageChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // User is selecting a new image, so clear any "removed" flag
    setIsImageRemoved(false);

    // Validate file type
    if (!file.type.startsWith('image/')) {
      api.error({
        message: 'Error',
        description: 'Please select a valid image file',
        showProgress: false,
      });
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      api.error({
        message: 'Error',
        description: 'Image size should be less than 5MB',
        showProgress: false,
      });
      return;
    }

    // Create preview immediately
    const reader = new FileReader();
    reader.onloadend = () => {
      setProfileImagePreview(reader.result);
    };
    reader.readAsDataURL(file);

    try {
      setUploadingImage(true);
      setUploadProgress(0);

      // Fetch the presigned URL from the server
      const signedUrl = await apiService.getPresignedForProfileUrl({
        fileName: file?.name,
        fileSize: file?.size,
        fileContentType: file?.type || 'image/jpeg',
      });

      if (signedUrl) {
        // Upload file using the signed URL
        const formData = new FormData();
        const fields = signedUrl?.preSigned?.fields;
        for (const key in fields) {
          if (Object.prototype.hasOwnProperty.call(fields, key)) {
            const value = fields[key];
            formData.append(key, value);
          }
        }
        formData.append('file', file);

        await apiService.uploadWithPresignedUrl(
          signedUrl?.preSigned?.url,
          formData,
          (progress) => {
            setUploadProgress(progress.percent);
          }
        );

        // Store the uploaded file info
        setProfileImage(file);
        setUploadedImageKey(signedUrl?.objectKey);

        api.success({
          message: 'Success',
          description: 'Profile image uploaded successfully',
          showProgress: false,
        });
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log('error on upload ', error);
      api.error({
        message: 'Error',
        description:
          error?.response?.data?.message ||
          error?.message ||
          'Failed to upload image',
        showProgress: false,
      });
      // Reset preview on error
      setProfileImagePreview(userProfile?.profileImageUrl || imguser);
    } finally {
      setUploadingImage(false);
      setUploadProgress(0);
    }
  };

  const handleRemoveImage = (e) => {
    e.stopPropagation(); // Prevent triggering the image click handler
    setProfileImage(null);
    setProfileImagePreview(imguser);
    setUploadedImageKey(null);
    setIsImageRemoved(true);
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const onSubmit = async (data) => {
    try {
      setLoading(true);

      // Determine if user wants to remove image
      // User explicitly removed existing image via the remove button
      const shouldRemoveImage =
        userProfile?.profileImageUrl && isImageRemoved;

      // Prepare request data with uploaded image key or remove flag
      let requestData;
      if (uploadedImageKey) {
        // Send the objectKey of the uploaded image
        requestData = {
          firstName: data.firstName,
          lastName: data.lastName,
          nameTitle: data.nameTitle,
          profileImage: uploadedImageKey,
        };
      } else if (shouldRemoveImage) {
        // If user wants to remove image, send a flag
        requestData = {
          firstName: data.firstName,
          lastName: data.lastName,
          nameTitle: data.nameTitle,
          removeProfileImage: true,
        };
      } else {
        requestData = {
          firstName: data.firstName,
          lastName: data.lastName,
          nameTitle: data.nameTitle,
        };
      }

      const response = await apiService.updateUserProfile(requestData);
      setUserProfile(response);
      setProfileImage(null);
      setProfileImagePreview(imguser);
      setUploadedImageKey(null);
      setIsImageRemoved(false);
      setLoading(false);
      api.success({
        message: 'Success',
        description: 'Profile Updated Successfully',
        showProgress: false,
      });
    } catch (error) {
      setLoading(false);
      api.error({
        message: 'Error',
        description:
          error?.msg || error?.message || 'Failed to update profile',
        showProgress: false,
      });
    }
  };

  return (
    <>
      {contextHolder}
      <div>
        {/* Page Header */}
        <div className="page-header">
          <div className="row">
            <div className="col-sm-7 col-6">
              <ul className="breadcrumb">
                <li className="breadcrumb-item">
                  <Link to="#">Dashboard </Link>
                </li>
                <li className="breadcrumb-item">
                  <i className="feather-chevron-right">
                    <FeatherIcon icon="chevron-right" />
                  </i>
                </li>
                <li className="breadcrumb-item active">Edit Profile</li>
              </ul>
            </div>
            <div className="col-sm-5 col-6 text-end m-b-30">
              <Button
                onClick={toggleChangePassword}
                type="primary"
                shape="round"
              >
                <i className="fa fa-edit pe-2" />
                {changePasswordVisible ? 'Back To Profile' : 'Change Password'}
              </Button>
            </div>
          </div>
        </div>

        {changePasswordVisible ? (
          <ChangeUserPassword />
        ) : (
          <form onSubmit={handleSubmit(onSubmit)}>
            {/* Profile Image Section */}
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
              <Col xs={24} sm={24} md={24}>
                <Form.Item label={<Typography.Text>Profile Image</Typography.Text>}>
                  <div style={{ textAlign: 'center' }}>
                    <div
                      style={{
                        display: 'inline-block',
                        position: 'relative',
                        cursor: uploadingImage ? 'not-allowed' : 'pointer',
                      }}
                      onClick={uploadingImage ? undefined : handleImageClick}
                    >
                      <img
                        src={displayImage}
                        className="profile-image-edit"
                        alt="Profile"
                        style={{
                          opacity: uploadingImage ? 0.6 : 1,
                        }}
                      />
                      {uploadingImage && (
                        <div
                          style={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            zIndex: 5,
                            textAlign: 'center',
                          }}
                        >
                          <Spin size="large" />
                          <div
                            style={{
                              marginTop: 8,
                              color: '#fff',
                              fontSize: 12,
                              fontWeight: 'bold',
                            }}
                          >
                            {uploadProgress}%
                          </div>
                        </div>
                      )}
                      {/* Remove Image Button - Top Right Corner */}
                      {(profileImage ||
                        uploadedImageKey ||
                        userProfile?.profileImageUrl) &&
                        !uploadingImage && (
                          <button
                            type="button"
                            className="btn btn-danger btn-sm rounded-circle position-absolute top-0 end-0 d-flex justify-content-center align-items-center shadow"
                            style={{ width: '32px', height: '32px', zIndex: 10 }}
                            onClick={handleRemoveImage}
                            title="Remove image"
                          >
                            <i className="fa fa-times text-white" />
                          </button>
                        )}
                      {/* Camera Icon - Bottom Right Corner */}
                      {!uploadingImage && (
                        <button
                          type="button"
                          className="btn btn-primary btn-sm rounded-circle position-absolute bottom-0 end-0 d-flex justify-content-center align-items-center shadow"
                          style={{ width: '40px', height: '40px', zIndex: 10 }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleImageClick();
                          }}
                          title="Change profile image"
                        >
                          <i className="fa fa-camera text-white" />
                        </button>
                      )}
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      style={{ display: 'none' }}
                    />
                    <Typography.Text
                      type="secondary"
                      style={{ fontSize: 14, display: 'block', marginTop: 8 }}
                    >
                      Click on the image to change your profile picture
                    </Typography.Text>
                  </div>
                </Form.Item>
              </Col>
            </Row>

            {/* Profile Fields */}
            <Row gutter={[16, 16]}>
              <Col md={12}>
                <div className="form-group">
                  <label
                    style={{
                      zIndex: 10000,
                    }}
                  >
                    Title <span className="login-danger">*</span>
                  </label>
                  <Controller
                    name="nameTitle"
                    control={control}
                    defaultValue={'Mr'}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        className="form-control menu-classM"
                        options={[
                          {
                            value: 'Mr',
                            label: 'Mr',
                          },
                          {
                            value: 'Mrs',
                            label: 'Mrs',
                          },
                          {
                            value: 'Ms',
                            label: 'Ms',
                          },
                          {
                            value: 'Dr',
                            label: 'Dr',
                          },
                          {
                            value: 'Sister',
                            label: 'Sister',
                          },
                        ]}
                      />
                    )}
                  />

                  <small style={{ color: 'red' }}>
                    {errors.nameTitle && <p>Please enter Title</p>}
                  </small>
                </div>
              </Col>
              <Col md={12}>
                <div className="form-group">
                  <label>
                    First Name <span className="login-danger">*</span>
                  </label>
                  <input
                    className="form-control"
                    type="text"
                    {...register('firstName', { required: true })}
                  />
                  <small style={{ color: 'red' }}>
                    {errors.firstName && <p>Please enter First name</p>}
                  </small>
                </div>
              </Col>
              <Col md={12}>
                <div className="form-group">
                  <label>
                    Last Name <span className="login-danger">*</span>
                  </label>
                  <input
                    className="form-control"
                    type="text"
                    {...register('lastName', { required: true })}
                  />
                  <small style={{ color: 'red' }}>
                    {errors.lastName && <p>Please enter Last name</p>}
                  </small>
                </div>
              </Col>
              <Col md={12}>
                <div className="form-group">
                  <label>
                    Email <span className="login-danger">*</span>
                  </label>
                  <input
                    className="form-control"
                    type="text"
                    disabled
                    {...register('email', {
                      required: 'Please enter Your Email!',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Please enter A Valid Email!',
                      },
                    })}
                  />
                  <small style={{ color: 'red' }}>
                    {errors.email?.message}
                  </small>
                </div>
              </Col>
            </Row>
            <Form.Item>
              <Button
                key="ok"
                type="primary"
                htmlType="submit"
                loading={loading || uploadingImage}
                disabled={uploadingImage}
                shape="round"
                size="large"
                className="float-right px-5"
              >
                Save
              </Button>
               <Button
                key="back"
                type="default"
                htmlType="submit"
                disabled={loading || uploadingImage}
                shape="round"
                size="large"
                className="float-right px-5"
                onClick={() => navigate('/user/profile')}
                style={{marginLeft:'2px'}}
              >
                Back to My Profile
              </Button>
            </Form.Item>
          </form>
        )}
      </div>
    </>
  );
};

export default EditProfile;
